import React, {
  useCallback, useContext, useEffect, useRef, FC, memo, useMemo,
} from 'react';
import { Textarea, TextareaValueData } from '@fluentai/textarea';
import { mergeClasses } from '@fluentui/react-components';
import { I18nContext, I18nModel } from '@bingads-webui/copilot-i18n';
import { ChatInputWithEntitySelection } from '@bingads-webui-react/copilot-entity-selection';
import {
  type BotServiceConfig,
  type ILogger,
} from '@bingads-webui/ads-copilot-shared-types';
import { LoadingContext } from './components/loading-provider/loading-context';
import { useAdsCopilotPilotMap } from './hooks/use-ads-copilot-pilot-map';

export interface IChatInputProps {
  sendDisabled?: boolean;
  inputDisabled?: boolean;
  className?: string;
  maxLength?: number;
  characterCountRevealThreshold?: number;
  value: string;
  onChange(_ev: any, { value }: TextareaValueData): void;
  postMessage(_evt: any, { value }: TextareaValueData): void;
  botServiceConfig?: BotServiceConfig;
  logger?: ILogger;
  isEntitySelectionMenuEnabled?: boolean;
  entitySelectionPerfMarker?: any;
  selectedPromptItem?: any;
  resetSelectedPromptItem?: () => void;
}

export const ChatInput: FC<IChatInputProps> = memo((props) => {
  const {
    botServiceConfig,
    className,
    maxLength,
    characterCountRevealThreshold = 450,
    value,
    inputDisabled,
    sendDisabled,
    onChange,
    postMessage,
    logger,
    isEntitySelectionMenuEnabled,
    entitySelectionPerfMarker,
    selectedPromptItem,
    resetSelectedPromptItem,
  } = props;
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const i18n = useContext(I18nContext) as I18nModel;
  const {
    isEntitySelectorEnabled,
    isPageContextEnabled,
    isPromptRefreshEnabled,
    isRefreshUIEnabled,
  } = useAdsCopilotPilotMap();

  useEffect(() => {
    textAreaRef.current?.focus();
  }, [sendDisabled]);

  useEffect(() => {
    logger?.trace(`[EntitySelection] isEntitySelectorEnabled: ${isEntitySelectorEnabled}`);
  }, [isEntitySelectorEnabled, logger]);

  const hotKeyHandler = useCallback((evt: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (evt.key === 'Enter' && !evt.shiftKey && value.trim().length > 0 && !sendDisabled) {
      postMessage(null, { value });
    }
  }, [postMessage, value]);

  const sendButtonProps = useMemo(() => ({
    disabled: sendDisabled || value.trim().length === 0,
  }), [sendDisabled, value]);

  const onSubmit = useCallback((_evt: any, { value: message }: TextareaValueData): void => {
    postMessage(_evt, { value: message });
  }, [postMessage]);

  const showCount = value.length >= characterCountRevealThreshold;

  const {
    isGeneratingAnswer,
    isTroubleshooting,
    isLiveAgentInProgress,
    onClickStopGenerating,
    onClickStopTroubleshooting,
    onEndLiveAgentChatClick,
  } = useContext(LoadingContext);

  const placeholder = useMemo(
    () => (isLiveAgentInProgress
      ? i18n.getString(_TL_('Message support'))
      : i18n.getString(_TL_('Message Copilot'))),
    [i18n, isLiveAgentInProgress]
  );

  return (
    isEntitySelectorEnabled
      ? <ChatInputWithEntitySelection
          maxLength={maxLength}
          characterCountRevealThreshold={characterCountRevealThreshold}
          inputDisabled={inputDisabled}
          sendDisabled={sendDisabled}
          postMessage={postMessage}
          onChange={onChange}
          botServiceConfig={botServiceConfig}
          logger={logger}
          isEntitySelectionMenuEnabled={isEntitySelectionMenuEnabled}
          isPageContextEnabled={isPageContextEnabled}
          perfMarker={entitySelectionPerfMarker}
          isDefaultValueEnabled={isPromptRefreshEnabled}
          selectedPromptItem={selectedPromptItem}
          resetSelectedPromptItem={resetSelectedPromptItem}
          isRefreshUIEnabled={isRefreshUIEnabled}
          isGeneratingAnswer={isGeneratingAnswer}
          isTroubleshooting={isTroubleshooting}
          isLiveAgentInProgress={isLiveAgentInProgress}
          onClickStopGenerating={onClickStopGenerating}
          onClickStopTroubleshooting={onClickStopTroubleshooting}
          onEndLiveAgentChatClick={onEndLiveAgentChatClick}
      />
      : <Textarea
          ref={textAreaRef}
          onKeyUp={hotKeyHandler}
          className={mergeClasses(className, 'chat-input')}
          disabled={inputDisabled}
          mode="sidecar"
          aria-label={placeholder}
          placeholder={placeholder}
          showCount={showCount}
          onSubmit={onSubmit}
          onChange={onChange}
          characterCount={value.length}
          value={value}
          maxLength={maxLength}
          send={sendButtonProps}
      />
  );
});

ChatInput.defaultProps = {
  inputDisabled: undefined,
  sendDisabled: undefined,
  className: '',
  maxLength: 500,
};
